import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Button } from './Button';
import ReactModal from 'react-modal';
import Modal from './Modal';
import { Hosts } from '../constants';
import Loader from './Loader';
import ReactMarkdown from 'react-markdown';
import { toast } from 'react-toastify';
import moment from 'moment';
import { Document, Page, Text, View, StyleSheet, Image, pdf } from '@react-pdf/renderer';
import Ai from '../store/models/Ai';
import { formatDateLocal } from '../utils';

// PDF Styles
const pdfStyles = StyleSheet.create({
  page: {
    padding: 40,
    fontSize: 10,
    fontFamily: 'Helvetica',
    backgroundColor: '#ffffff',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 15,
    paddingBottom: 10,
    borderBottom: '2pt solid #000000',
  },
  headerLeft: {
    flex: 1,
  },
  title: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 4,
    color: '#000000',
  },
  subtitle: {
    fontSize: 9,
    color: '#666666',
  },
  logo: {
    width: 80,
    height: 'auto',
    marginLeft: 20,
  },
  samplingNotice: {
    backgroundColor: '#e8f5f1',
    borderLeft: '3pt solid #18C99D',
    padding: 8,
    marginBottom: 12,
    fontSize: 8,
  },
  samplingText: {
    color: '#000000',
    lineHeight: 1.3,
  },
  h1: {
    fontSize: 14,
    fontWeight: 'bold',
    marginTop: 14,
    marginBottom: 8,
    color: '#000000',
  },
  h2: {
    fontSize: 12,
    fontWeight: 'bold',
    marginTop: 12,
    marginBottom: 7,
    color: '#000000',
  },
  h3: {
    fontSize: 11,
    fontWeight: 'bold',
    marginTop: 10,
    marginBottom: 6,
    color: '#000000',
  },
  paragraph: {
    marginBottom: 3,
    lineHeight: 1.2,
    color: '#000000',
  },
  listItem: {
    marginBottom: 2,
    marginLeft: 15,
    lineHeight: 1.2,
  },
  bold: {
    fontWeight: 'bold',
  },
  table: {
    marginTop: 8,
    marginBottom: 8,
    width: '100%',
  },
  tableRow: {
    flexDirection: 'row',
    borderBottom: '1pt solid #e0e0e0',
  },
  tableHeaderRow: {
    flexDirection: 'row',
    borderBottom: '2pt solid #18C99D',
    backgroundColor: '#f5f5f5',
  },
  tableCell: {
    flex: 1,
    padding: 6,
    fontSize: 9,
    lineHeight: 1.3,
  },
  tableHeaderCell: {
    flex: 1,
    padding: 6,
    fontSize: 9,
    fontWeight: 'bold',
    lineHeight: 1.3,
  },
});

// PDF Document Component
const PDFDocument = ({ report, reportTitle, dateRangeInfo, prompts }) => {
  // Parse markdown to structured content
  const parseMarkdown = (text) => {
    const lines = text.split('\n');
    const elements = [];
    let currentList = [];
    let currentTable = null;
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      
      if (!line) {
        if (currentList.length > 0) {
          elements.push({ type: 'list', items: [...currentList] });
          currentList = [];
        }
        if (currentTable) {
          elements.push({ type: 'table', ...currentTable });
          currentTable = null;
        }
        continue;
      }
      
      // Table rows (lines starting with |)
      if (line.startsWith('|') && line.endsWith('|')) {
        if (currentList.length > 0) {
          elements.push({ type: 'list', items: [...currentList] });
          currentList = [];
        }
        
        const cells = line.split('|').slice(1, -1).map(cell => cell.trim());
        
        // Check if this is a separator row
        if (cells.every(cell => cell.match(/^[-:]+$/))) {
          // Skip separator rows
          continue;
        }
        
        if (!currentTable) {
          // First row is header
          currentTable = {
            headers: cells,
            rows: []
          };
        } else {
          // Data row
          currentTable.rows.push(cells);
        }
      }
      // Headers
      else if (line.startsWith('### ')) {
        if (currentList.length > 0) {
          elements.push({ type: 'list', items: [...currentList] });
          currentList = [];
        }
        if (currentTable) {
          elements.push({ type: 'table', ...currentTable });
          currentTable = null;
        }
        elements.push({ type: 'h3', content: line.substring(4) });
      } else if (line.startsWith('## ')) {
        if (currentList.length > 0) {
          elements.push({ type: 'list', items: [...currentList] });
          currentList = [];
        }
        if (currentTable) {
          elements.push({ type: 'table', ...currentTable });
          currentTable = null;
        }
        elements.push({ type: 'h2', content: line.substring(3) });
      } else if (line.startsWith('# ')) {
        if (currentList.length > 0) {
          elements.push({ type: 'list', items: [...currentList] });
          currentList = [];
        }
        if (currentTable) {
          elements.push({ type: 'table', ...currentTable });
          currentTable = null;
        }
        elements.push({ type: 'h1', content: line.substring(2) });
      }
      // List items
      else if (line.startsWith('- ') || line.match(/^\d+\. /)) {
        if (currentTable) {
          elements.push({ type: 'table', ...currentTable });
          currentTable = null;
        }
        const content = line.replace(/^[-\d]+\.\s/, '');
        currentList.push(content);
      }
      // Regular paragraph
      else {
        if (currentList.length > 0) {
          elements.push({ type: 'list', items: [...currentList] });
          currentList = [];
        }
        if (currentTable) {
          elements.push({ type: 'table', ...currentTable });
          currentTable = null;
        }
        elements.push({ type: 'paragraph', content: line });
      }
    }
    
    if (currentList.length > 0) {
      elements.push({ type: 'list', items: currentList });
    }
    if (currentTable) {
      elements.push({ type: 'table', ...currentTable });
    }
    
    return elements;
  };
  
  // Process text with bold markers
  const processTextWithBold = (text) => {
    const parts = [];
    const regex = /\*\*(.*?)\*\*/g;
    let lastIndex = 0;
    let match;
    
    while ((match = regex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push({ text: text.substring(lastIndex, match.index), bold: false });
      }
      parts.push({ text: match[1], bold: true });
      lastIndex = regex.lastIndex;
    }
    
    if (lastIndex < text.length) {
      parts.push({ text: text.substring(lastIndex), bold: false });
    }
    
    return parts.length > 0 ? parts : [{ text, bold: false }];
  };
  
  const content = parseMarkdown(report);
  
  return (
    <Document>
      <Page size="A4" style={pdfStyles.page}>
        {/* Header */}
        <View style={pdfStyles.header}>
          <View style={pdfStyles.headerLeft}>
            <Text style={pdfStyles.title}>{reportTitle}</Text>
            {dateRangeInfo && (
              <Text style={pdfStyles.subtitle}>
                {dateRangeInfo.start} - {dateRangeInfo.end} • {dateRangeInfo.totalPrompts.toLocaleString()} prompts
              </Text>
            )}
          </View>
          <Image 
            src={`${window.location.origin}/images/edgecloud-logo-mobile.png`}
            style={pdfStyles.logo}
          />
        </View>
        
        {/* Sampling Notice */}
        {prompts && prompts.length > 500 && (
          <View style={pdfStyles.samplingNotice}>
            <Text style={pdfStyles.samplingText}>
              <Text style={pdfStyles.bold}>Note:</Text> This report analyzed 500 prompts evenly sampled from {prompts.length} total prompts across the date range.
            </Text>
          </View>
        )}
        
        {/* Content */}
        {content.map((element, index) => {
          if (element.type === 'h1') {
            return (
              <Text key={index} style={pdfStyles.h1}>
                {element.content}
              </Text>
            );
          } else if (element.type === 'h2') {
            return (
              <Text key={index} style={pdfStyles.h2}>
                {element.content}
              </Text>
            );
          } else if (element.type === 'h3') {
            return (
              <Text key={index} style={pdfStyles.h3}>
                {element.content}
              </Text>
            );
          } else if (element.type === 'list') {
            return (
              <View key={index}>
                {element.items.map((item, itemIndex) => {
                  const textParts = processTextWithBold(item);
                  return (
                    <Text key={itemIndex} style={pdfStyles.listItem}>
                      • {textParts.map((part, partIndex) => (
                        <Text key={partIndex} style={part.bold ? pdfStyles.bold : {}}>
                          {part.text}
                        </Text>
                      ))}
                    </Text>
                  );
                })}
              </View>
            );
          } else if (element.type === 'paragraph') {
            const textParts = processTextWithBold(element.content);
            return (
              <Text key={index} style={pdfStyles.paragraph}>
                {textParts.map((part, partIndex) => (
                  <Text key={partIndex} style={part.bold ? pdfStyles.bold : {}}>
                    {part.text}
                  </Text>
                ))}
              </Text>
            );
          } else if (element.type === 'table') {
            return (
              <View key={index} style={pdfStyles.table}>
                {/* Table Header */}
                <View style={pdfStyles.tableHeaderRow}>
                  {element.headers.map((header, headerIndex) => (
                    <Text key={headerIndex} style={pdfStyles.tableHeaderCell}>
                      {header}
                    </Text>
                  ))}
                </View>
                {/* Table Rows */}
                {element.rows.map((row, rowIndex) => (
                  <View key={rowIndex} style={pdfStyles.tableRow}>
                    {row.map((cell, cellIndex) => (
                      <Text key={cellIndex} style={pdfStyles.tableCell}>
                        {cell}
                      </Text>
                    ))}
                  </View>
                ))}
              </View>
            );
          }
          return null;
        })}
      </Page>
    </Document>
  );
};

export const AiReportButton = ({
  prompts: providedPrompts,
  chatbotId,
  accessToken,
  projectId,
  startDate,
  endDate,
  buttonTitle = "AI Report",
  buttonColor = "blue-outline",
  buttonSize = "medium",
  reportTitle = "Chatbot AI Report",
  customPromptGenerator = null
}) => {
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [report, setReport] = useState('');
  const [error, setError] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [prompts, setPrompts] = useState(providedPrompts);
  const [isLoadingPrompts, setIsLoadingPrompts] = useState(false);
  const loadedDateRange = useRef({ startDate: null, endDate: null });
  
  // Refs to track ongoing operations for cleanup
  const abortControllerRef = useRef(null);
  const readerRef = useRef(null);
  const hasTriggeredGeneration = useRef(false);
  
  // Update prompts when providedPrompts changes
  useEffect(() => {
    if (providedPrompts) {
      setPrompts(providedPrompts);
    }
  }, [providedPrompts]);

  // Load prompts when modal opens (lazy loading)
  useEffect(() => {
    if (isModalOpen && needsPromptLoad()) {
      hasTriggeredGeneration.current = false;
      loadPromptsIfNeeded().catch(() => {
        // Error already handled in loadPromptsIfNeeded
      });
    } else if (isModalOpen && !needsPromptLoad() && prompts && prompts.length > 0) {
      // Prompts already available, trigger generation
      hasTriggeredGeneration.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen]);

  // Auto-generate report once prompts are loaded
  useEffect(() => {
    if (
      isModalOpen &&
      !isLoadingPrompts &&
      prompts &&
      prompts.length > 0 &&
      !isGenerating &&
      !report &&
      !error &&
      !hasTriggeredGeneration.current
    ) {
      hasTriggeredGeneration.current = true;
      generateReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen, isLoadingPrompts, prompts, isGenerating, report, error]);
  
  // Check if prompts need to be loaded
  const needsPromptLoad = () => {
    // If prompts are already provided, use them
    if (providedPrompts && providedPrompts.length > 0) {
      return false;
    }
    
    // If we have prompts loaded, check if date range matches
    if (prompts && prompts.length > 0 && startDate && endDate) {
      const currentStart = formatDateLocal(startDate);
      const currentEnd = formatDateLocal(endDate);
      const loadedStart = loadedDateRange.current.startDate;
      const loadedEnd = loadedDateRange.current.endDate;
      
      if (loadedStart === currentStart && loadedEnd === currentEnd) {
        return false; // Already loaded with matching date range
      }
    }
    
    // Need to load if we have the required props
    return chatbotId && projectId && startDate && endDate;
  };
  
  // Load prompts if needed
  const loadPromptsIfNeeded = async () => {
    if (!needsPromptLoad()) {
      return prompts;
    }
    
    setIsLoadingPrompts(true);
    try {
      const sDate = formatDateLocal(startDate);
      const eDate = formatDateLocal(endDate);
      const res = await dispatch(Ai.actions.fetchChatbotPrompts(chatbotId, projectId, { 
        start_timestamp: sDate, 
        end_timestamp: eDate 
      }));
      
      setPrompts(res);
      loadedDateRange.current = { startDate: sDate, endDate: eDate };
      return res;
    } catch (e) {
      console.error('Error loading prompts:', e);
      toast.error('Failed to load prompts for report');
      throw e;
    } finally {
      setIsLoadingPrompts(false);
    }
  };
  
  // Calculate date range and prompt count from prompts
  // Use the selected dates (startDate/endDate) instead of prompt timestamps
  // to ensure the displayed range matches what the user selected
  const dateRangeInfo = useMemo(() => {
    if (!prompts || prompts.length === 0) return null;
    
    // Use the selected dates if available, otherwise fall back to prompt timestamps
    if (startDate && endDate) {
      return {
        start: moment(startDate).format('MMM DD, YYYY'),
        end: moment(endDate).format('MMM DD, YYYY'),
        totalPrompts: prompts.length
      };
    }
    
    // Fallback to prompt timestamps if dates not provided
    const start = prompts[prompts.length - 1]?.timestamp;
    const end = prompts[0]?.timestamp;
    
    if (!start || !end) return null;
    
    return {
      start: moment(start).format('MMM DD, YYYY'),
      end: moment(end).format('MMM DD, YYYY'),
      totalPrompts: prompts.length
    };
  }, [prompts, startDate, endDate]);

  /**
   * Default prompt generator that creates a summary prompt from the prompts data
   */
  const defaultPromptGenerator = (promptsData) => {
    if (!promptsData || promptsData.length === 0) {
      return "No prompts data available to analyze.";
    }

    // Filter out summary reports (chatbot-generated reports)
    // These typically contain markdown headers, structured lists, or are very long formatted text
    const userPrompts = promptsData.filter(p => {
      const question = p.question || '';
      // Filter out prompts that look like AI-generated reports
      const isReport = 
        question.includes('## Topic Overview') ||
        question.includes('## Topics by Frequency') ||
        question.includes('**1.') ||
        question.includes('**2.') ||
        question.includes('Most Discussed Topics') ||
        question.includes('Moderately Discussed Topics') ||
        (question.length > 500 && question.includes('questions)'));
      return !isReport;
    });

    const totalPrompts = userPrompts.length;
    const dateRange = {
      start: userPrompts[userPrompts.length - 1]?.timestamp,
      end: userPrompts[0]?.timestamp
    };

    // Smart sampling: distribute samples evenly across the dataset
    // Limit to 500 prompts max (only questions, so we have plenty of token budget)
    const maxSamples = 500;
    let sampledPrompts = [];
    
    if (totalPrompts <= maxSamples) {
      // Use all prompts if we're under the limit
      sampledPrompts = userPrompts;
    } else {
      // Sample evenly across the date range
      const step = totalPrompts / maxSamples;
      for (let i = 0; i < maxSamples; i++) {
        const index = Math.floor(i * step);
        if (index < totalPrompts) {
          sampledPrompts.push(userPrompts[index]);
        }
      }
    }

    const sampleSize = sampledPrompts.length;
    const samplePrompts = sampledPrompts.map((p, idx) => {
      return `${idx + 1}. "${p.question}"`;
    }).join('\n');

    const prompt = `You are an analytical assistant. You will receive a list of user prompts sent to a chatbot. 

Your job is to generate a concise, high-quality usage report based ONLY on these prompts.

Do NOT assume anything that cannot be inferred directly from the prompts.

**Dataset Information:**
- Total Prompts: ${totalPrompts}
- Date Range: ${moment(dateRange.start).format('MMM DD, YYYY')} to ${moment(dateRange.end).format('MMM DD, YYYY')}
- Sample Size: ${sampleSize} prompts${sampleSize < totalPrompts ? ' (evenly sampled across the date range)' : ' (full dataset)'}

${sampleSize < totalPrompts ? `\n**Note:** This analysis is based on ${sampleSize} prompts evenly sampled from ${totalPrompts} total prompts. When providing percentages or counts, extrapolate from the sample to represent the full dataset of ${totalPrompts} prompts.\n` : ''}
**User Questions:**
${samplePrompts}

**Required Report Structure:**

Follow this exact structure:

## Summary

Summarize the main themes, most common user needs, and notable trends. Be concise and direct. Skip generic statements like "this report provides" or "comprehensive overview".

## Top topics

Group prompts into meaningful categories. For each category, provide:
- Category name
- Short description
- Approximate percentage of prompts in this category (extrapolated to the full dataset of ${totalPrompts} prompts if using a sample)
- Maximum 2 example prompts from the data (quoted exactly, no duplicates)

Format each category as:
**1. Category Name** (X% of prompts)
Brief description of this category.

- "[exact quote from prompts]"
- "[exact quote from prompts]"

IMPORTANT: Use maximum 2 examples per category and ensure no example is duplicated across different categories.

List categories in descending order by frequency, numbered sequentially (1, 2, 3, etc.).

## User sentiment

Provide counts or percentages of positive, neutral, and negative prompts. Identify categories or themes showing both high positive and high negative sentiment. Note any sentiment trends visible in the data.

Format as:
- Positive: X prompts (Y%)
- Neutral: X prompts (Y%)
- Negative: X prompts (Y%)

Then identify:
- Categories with notably high positive sentiment
- Categories with notably high negative sentiment
- Overall sentiment trends

## Positive Patterns & Opportunities

Identify positive aspects, successful engagement patterns, and opportunities visible in the prompts. Highlight:
- Topics or categories showing positive sentiment or high engagement
- Successful user behaviors or patterns
- Areas where users are satisfied or showing interest
- Opportunities for growth or improvement based on user interest

Focus on constructive insights and what's working well, while being accurate to the data.

## Trending Issues

Identify recurring frustrations, confusion points, or frequent requests. Highlight any new or emerging topics compared to the rest of the dataset.

## Representative Examples

List 6–10 prompts that are the most illustrative of:
- Common questions
- Positive engagement examples (satisfied users, successful interactions, positive feedback)
- Edge cases
- Unusual or outlier prompts
- Pain points

Balance the selection to include both positive examples and areas for improvement. Use direct quotes. Format as:
- "[exact quote]"
- "[exact quote]"
- "[exact quote]"

**Constraints:**
- Base ALL analysis strictly on the provided prompts
- Do not invent data
- Do not reference anything outside the prompt list
- Be concise but informative
- Do NOT use emojis
- Use real examples from the questions provided
- Format in clean markdown
- Skip generic introductory statements - be direct and get straight to the content`;

    return prompt;
  };

  /**
   * Handle opening the modal - loads prompts if needed (lazy loading)
   */
  const handleOpenModal = () => {
    hasTriggeredGeneration.current = false;
    setIsModalOpen(true);
    setReport('');
    setError(null);
    setIsGenerating(false);
  };

  /**
   * Generate the AI report by calling the chatbot's completion API
   */
  const generateReport = async () => {
    // Prompts should already be loaded when modal opens
    const promptsToUse = prompts;
    
    if (!promptsToUse || promptsToUse.length === 0) {
      // If still loading, wait a bit
      if (isLoadingPrompts) {
        toast.info('Please wait for prompts to load...');
        return;
      }
      toast.error('No prompts data available to generate a report');
      return;
    }

    // Validation check
    if (!chatbotId || !accessToken) {
      toast.error('Missing chatbot configuration');
      return;
    }

    // Validate date range (max 31 days)
    const startDate = moment(promptsToUse[promptsToUse.length - 1]?.timestamp);
    const endDate = moment(promptsToUse[0]?.timestamp);
    const daysDifference = endDate.diff(startDate, 'days');
    
    if (daysDifference > 31) {
      toast.error('Date range exceeds 31 days. Please select a shorter time period (maximum 31 days).');
      return;
    }

    setIsGenerating(true);
    setReport('');
    setError(null);

    // Create AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      // Generate the prompt using prompts from the current project
      const promptGenerator = customPromptGenerator || defaultPromptGenerator;
      const analysisPrompt = promptGenerator(promptsToUse); // Uses prompts from current project

      // API endpoint for report generation using project's credentials
      const apiUrl = `${Hosts.CHATBOT_API}/chatbot/${chatbotId}/chat/completions`;
      
      const response = await fetch(apiUrl, {
        signal: abortController.signal,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          messages: [
            {
              role: 'system',
              content: 'You are a data analyst specializing in topic extraction and categorization. Your role is to identify what users are asking about, categorize topics, and provide statistics with numbers and percentages. Focus on topic identification, not quality analysis.'
            },
            {
              role: 'user',
              content: analysisPrompt
            }
          ],
          max_tokens: 3000,
          temperature: 0.7,
          stream: true
        })
      });

      if (!response.ok) {
        let errorMessage = 'Failed to generate report';
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch (e) {
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      if (!response.body) {
        throw new Error('Response body is not available');
      }

      // Handle streaming response (SSE format)
      const reader = response.body.getReader();
      readerRef.current = reader;
      const decoder = new TextDecoder();
      let accumulatedReport = '';
      let buffer = ''; // Buffer for incomplete lines
      let hasReceivedContent = false;
      let currentEvent = null;
      let currentData = '';

      // Helper function to process accumulated SSE data
      const processSSEData = (data) => {
        if (!data || data.trim() === '' || data.trim() === '[DONE]') {
          return;
        }

        try {
          const parsed = JSON.parse(data);
          const content = parsed.choices?.[0]?.delta?.content;
          
          if (content) {
            accumulatedReport += content;
            hasReceivedContent = true;
            setReport(accumulatedReport);
          }
        } catch (e) {
          // Skip invalid JSON chunks - this can happen if JSON is still incomplete
          // Only log if it's not a syntax error (which indicates incomplete data)
          if (!e.message.includes('unterminated') && !e.message.includes('Unexpected end')) {
            console.warn('Failed to parse SSE data:', e, 'Data:', data.substring(0, 100));
          }
        }
      };

      // Helper function to process SSE lines
      const processSSELine = (line, isFinal) => {
        if (!line) {
          // Empty line indicates end of event - process accumulated data
          if (currentData) {
            processSSEData(currentData);
            currentData = '';
          }
          currentEvent = null;
          return;
        }

        if (line.startsWith('event: ')) {
          // Track event type (e.g., "agent.start", "message", etc.)
          currentEvent = line.substring(7).trim();
          return;
        }

        if (line.startsWith('data: ')) {
          // Accumulate data (SSE can have multiple data lines)
          const data = line.substring(6);
          currentData += data;
          // If this is the final chunk and we have data, process it immediately
          if (isFinal) {
            processSSEData(currentData);
            currentData = '';
          }
          return;
        }

        // Ignore other SSE fields like 'id:', 'retry:', etc.
        if (line.startsWith('id:') || line.startsWith('retry:')) {
          return;
        }
      };

      try {
        while (true) {
          // Check if request was aborted
          if (abortControllerRef.current?.signal.aborted) {
            reader.cancel();
            break;
          }
          
          const { done, value } = await reader.read();
          
          if (done) {
            // Process any remaining buffer
            if (buffer.trim()) {
              const lines = buffer.split('\n');
              for (const line of lines) {
                processSSELine(line.trim(), true);
              }
            }
            // Process any remaining data
            if (currentData) {
              processSSEData(currentData);
            }
            break;
          }

          if (!value) {
            continue;
          }

          const chunk = decoder.decode(value, { stream: true });
          buffer += chunk;
          const lines = buffer.split('\n');

          // Keep the last line in buffer if it doesn't end with \n (incomplete)
          buffer = lines.pop() || '';

          for (const line of lines) {
            processSSELine(line.trim(), false);
          }
        }
      } finally {
        // Always release the reader
        reader.releaseLock();
      }

      if (!hasReceivedContent && !accumulatedReport) {
        throw new Error('No report content was received from the server');
      }

    } catch (err) {
      // Don't show error if request was aborted (user closed modal)
      if (err.name === 'AbortError' || abortControllerRef.current?.signal.aborted) {
        return;
      }
      console.error('Error generating AI report:', err);
      setError(err.message || 'Failed to generate report. Please try again.');
      toast.error(err.message || 'Failed to generate report');
    } finally {
      // Only reset if not aborted
      if (!abortControllerRef.current?.signal.aborted) {
        setIsGenerating(false);
      }
      // Clean up refs
      abortControllerRef.current = null;
      readerRef.current = null;
    }
  };

  const handleClose = () => {
    // Abort any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    
    // Cancel any ongoing reader
    if (readerRef.current) {
      readerRef.current.cancel().catch(() => {
        // Ignore errors from canceling
      });
      readerRef.current = null;
    }
    
    // Reset all state
    hasTriggeredGeneration.current = false;
    setIsModalOpen(false);
    setIsGenerating(false);
    setReport('');
    setError(null);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Cleanup on component unmount
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (readerRef.current) {
        readerRef.current.cancel().catch(() => {});
      }
    };
  }, []);

  const handleDownloadReport = async () => {
    if (!report) return;

    setIsDownloading(true);
    
    try {
      // Generate PDF blob
      const blob = await pdf(
        <PDFDocument 
          report={report}
          reportTitle={reportTitle}
          dateRangeInfo={dateRangeInfo}
          prompts={prompts}
        />
      ).toBlob();
      
      // Trigger download
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `chatbot-topics-report-${moment().format('YYYY-MM-DD')}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
      
      toast.success('Report downloaded as PDF!');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
      <Button
        className="AiReportButton"
        color={buttonColor}
        title={buttonTitle}
        onClick={handleOpenModal}
        size={buttonSize}
        icon={<img src="/images/icon-spark.png" alt="" style={{ width: '16px', height: '16px', marginRight: '8px' }} />}
      />

      <ReactModal
        isOpen={isModalOpen}
        onRequestClose={handleClose}
        ariaHideApp={false}
        overlayClassName="modal-overlay"
        className="modal-container"
      >
        <Modal onRequestClose={handleClose} className="AiReportModal">
          <div className="AiReportModal__content">
            <div className="AiReportModal__header">
              <div className="AiReportModal__header-content">
                <h2>{reportTitle}</h2>
                {dateRangeInfo && (
                  <div className="AiReportModal__date-range">
                    {dateRangeInfo.start} - {dateRangeInfo.end} • {dateRangeInfo.totalPrompts.toLocaleString()} prompts
                  </div>
                )}
              </div>
              <img 
                src="/images/edgecloud-logo-mobile.svg" 
                alt="Theta EdgeCloud" 
                className="AiReportModal__logo"
              />
            </div>

            <div className="AiReportModal__body">
              {(isLoadingPrompts || isGenerating) && !report && !error && (
                <div className="AiReportModal__loading">
                  <Loader size="medium" color="green" position="left" />
                </div>
              )}

              {!isLoadingPrompts && !isGenerating && !prompts && !error && (
                <div className="AiReportModal__error">
                  <p>No prompts available to generate a report.</p>
                </div>
              )}

              {error && !isGenerating && (
                <div className="AiReportModal__error">
                  <p>{error}</p>
                  <Button
                    color="green-outline"
                    title="Try Again"
                    onClick={generateReport}
                  />
                </div>
              )}

              {report && !error && (
                <>
                  {prompts && prompts.length > 500 && (
                    <div className="AiReportModal__sample-notice">
                      <strong>Note:</strong> This report analyzed 500 prompts evenly sampled from {prompts.length} total prompts across the date range.
                    </div>
                  )}
                  <div className="AiReportModal__report">
                    <ReactMarkdown>{report}</ReactMarkdown>
                  </div>
                </>
              )}
            </div>

            {!isLoadingPrompts && !isGenerating && (
              <div className="AiReportModal__footer">
                {report && !error ? (
                  <>
                    <Button
                      color="green-outline"
                      title={isDownloading ? "Generating PDF..." : "Download PDF"}
                      onClick={handleDownloadReport}
                      loading={isDownloading}
                      disabled={isDownloading}
                    />
                    <Button
                      color="grey-outline"
                      title="Close"
                      onClick={handleClose}
                      disabled={isDownloading}
                    />
                  </>
                ) : (
                  <Button
                    color="grey-outline"
                    title="Close"
                    onClick={handleClose}
                  />
                )}
              </div>
            )}
          </div>
        </Modal>
      </ReactModal>
    </>
  );
};

export default AiReportButton;

